import {
  useGetGeneratedDiagramFiltersQuery,
  useGetStoredDiagramFiltersQuery,
} from '../api/enhanced/enhanced-bowtie-api';
import type { DiagramFilterDto } from '../api/generated/bowtie-api';
import { useDiagramContext } from '../context/diagram.context';
import type { DiagramDisplayFilterState } from '../redux/slices/diagram';

/**
 * Custom hook that provides diagram filter data based on the current diagram context.
 *
 * This hook determines whether the current diagram is generated or stored,
 * fetches the appropriate filter data using the corresponding query hook,
 * and builds the diagram filter state.
 *
 * @returns An object containing:
 *   - `loading` - Boolean indicating if filter data is being fetched
 *   - `diagramFilterData` - The processed diagram filter state or undefined if not available
 */
const useDiagramFilterData = () => {
  const { diagramId, recordId } = useDiagramContext();
  const isGeneratedDiagram = Boolean(diagramId && !recordId);
  const isStoredDiagram = Boolean(recordId && !diagramId);

  const { data: storedDiagramFilters, isFetching: isFetchingStoredDiagramFilters } = useGetStoredDiagramFiltersQuery(
    { id: recordId! },
    { skip: !isStoredDiagram }
  );

  const { data: generatedDiagramFilters, isFetching: isFetchingGeneratedDiagramFilters } =
    useGetGeneratedDiagramFiltersQuery({ id: diagramId! }, { skip: !isGeneratedDiagram });

  const loading =
    (isGeneratedDiagram && isFetchingGeneratedDiagramFilters) || (isStoredDiagram && isFetchingStoredDiagramFilters);

  let diagramFilterData: DiagramDisplayFilterState | undefined;
  if (isGeneratedDiagram) {
    diagramFilterData = buildDiagramFilterData(generatedDiagramFilters);
  } else if (isStoredDiagram) {
    diagramFilterData = buildDiagramFilterData(storedDiagramFilters);
  }

  return {
    loading,
    diagramFilterData,
    isGeneratedDiagram,
    isStoredDiagram,
  };
};

export default useDiagramFilterData;

/**
 * Builds the diagram filter state object from the provided DTO.
 *
 * This function takes a diagram filter data transfer object and converts it into
 * a state object that can be used by the diagram display components.
 *
 * @param {DiagramFilterDto} [data] - The diagram filter data transfer object.
 * @returns {DiagramDisplayFilterState | undefined} A state object containing boolean flags for diagram display options,
 * or undefined if no data is provided.
 *
 * @typeParam DiagramFilterDto - The data transfer object type for diagram filters
 * @typeParam DiagramDisplayFilterState - The state type for diagram display filters
 */
const buildDiagramFilterData = (data?: DiagramFilterDto): DiagramDisplayFilterState | undefined => {
  const { displayFilter = {} } = data ?? {};
  return {
    showOnlyCriticalControls: Boolean(displayFilter?.showOnlyCriticalControls),
    showControlTypes: Boolean(displayFilter?.showControlTypes),
    showControlEffectiveness: Boolean(displayFilter?.showControlEffectiveness),
    showControlsInSingleRow: Boolean(displayFilter?.showControlsInSingleRow),
  };
};
